import { Box, Chip, CircularProgress, LinearProgress, Link, type SxProps, Typography } from '@mui/material'
import { CheckCircle, PanoramaFishEye } from '@mui/icons-material'
import ErrorIcon from '@mui/icons-material/Error'
import { useEffect, useState } from 'react'
import { type PlanOrganizationTask, PlanOrganizationTaskName, PlanOrganizationTaskStatus } from '../../../models/PlanOrganizationTask'
import IconAccordion from '../../customComponents/IconAccordion'
import { TranslateDate } from '../../../utils/dateUtils'
import { AnnualPlanTabs } from './PlanningDetails'
import { ImplementationDetailsTabs } from '../implementation/ImplementationDetails'
import { generatePath } from 'react-router'
import { useNavigate, useParams } from 'react-router-dom'
import { AppRoutes } from '../../Routes'
import {
  CETA_PDS_INSTRUCTIONS_URL,
  COACHING_CAPACITY_TOOL_INSTRUCTIONS_URL,
  P2P_DATA_COLLECTION_INSTRUCTIONS_URL,
  RCN_ANNUAL_ACTIVITIES_URL
} from '../../../constants/gvsuLinks'

interface Props {
  organizationName: string
  organizationTasks: PlanOrganizationTask[]
  changeTab: (tab: number) => void
  implementationPhase?: boolean
}

const completedContractStatuses: PlanOrganizationTaskStatus[] = [
  PlanOrganizationTaskStatus.Submitted,
  PlanOrganizationTaskStatus.NeedsRevision,
  PlanOrganizationTaskStatus.Accepted,
  PlanOrganizationTaskStatus.UpdatesReady,
  PlanOrganizationTaskStatus.UpdatesInProgress,
  PlanOrganizationTaskStatus.UpdatesSubmitted,
  PlanOrganizationTaskStatus.UpdatesNeedsRevision,
  PlanOrganizationTaskStatus.TrackingInProgress,
  PlanOrganizationTaskStatus.TrackingCompleted
]

const completedImplementationStatuses: PlanOrganizationTaskStatus[] = [
  PlanOrganizationTaskStatus.UpdatesSubmitted,
  PlanOrganizationTaskStatus.UpdatesNeedsRevision,
  PlanOrganizationTaskStatus.TrackingInProgress,
  PlanOrganizationTaskStatus.TrackingCompleted
]

const externalTaskUrls: Partial<Record<PlanOrganizationTaskName, string>> = {
  [PlanOrganizationTaskName.FocusStudentDataBOY]: RCN_ANNUAL_ACTIVITIES_URL,
  [PlanOrganizationTaskName.FocusStudentDataEOY]: RCN_ANNUAL_ACTIVITIES_URL,
  [PlanOrganizationTaskName.CetaBOY]: CETA_PDS_INSTRUCTIONS_URL,
  [PlanOrganizationTaskName.CetaEOY]: CETA_PDS_INSTRUCTIONS_URL,
  [PlanOrganizationTaskName.CoachingCapacityTool]: COACHING_CAPACITY_TOOL_INSTRUCTIONS_URL,
  [PlanOrganizationTaskName.PeerToPeerDataCollectionSystem]: P2P_DATA_COLLECTION_INSTRUCTIONS_URL
}

export function OrganizationTaskProgress (props: Props): JSX.Element {
  const nav = useNavigate()
  const { rcnId, planId } = useParams()
  const currentDate: Date = new Date()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [done, setDone] = useState<PlanOrganizationTask[]>([])
  const [dueSoon, setDueSoon] = useState<PlanOrganizationTask[]>([])
  const [dueLater, setDueLater] = useState<PlanOrganizationTask[]>([])
  const [statusColor, setStatusColor] = useState('')
  const [cardIcon, setCardIcon] = useState<JSX.Element>(<></>)
  const [percentProgress, setPercentProgress] = useState(0)
  const [orgKey, setOrgKey] = useState(0)
  const completedStatuses = props.implementationPhase === true ? completedImplementationStatuses : completedContractStatuses
  const sortedTasks: PlanOrganizationTask[] = props.organizationTasks.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())

  useEffect(() => {
    setIsLoading(true)
    const done: PlanOrganizationTask[] = []
    const now: PlanOrganizationTask[] = []
    const upcoming: PlanOrganizationTask[] = []
    let completedTasks: number = 0

    sortedTasks.forEach(ot => {
      if (orgKey === 0) {
        setOrgKey(ot.organizationId)
      }

      const taskDue: Date = TranslateDate(ot.dueDate)
      if (completedStatuses.includes(ot.status)) {
        completedTasks++
      }

      if (completedStatuses.includes(ot.status)) {
        done.push(ot)
      } else if (taskDue < get1WeekDate()) {
        now.push(ot)
      } else {
        upcoming.push(ot)
      }
    })

    setPercentProgress(Math.round((completedTasks / props.organizationTasks.length) * 100))
    setDone(done)
    setDueSoon(now)
    setDueLater(upcoming)
    getIconAndColor()
    setIsLoading(false)
  }, [props.organizationTasks])

  const taskIcon = (task: PlanOrganizationTask): JSX.Element => {
    const taskDue: Date = TranslateDate(task.dueDate)
    if (completedStatuses.includes(task.status)) {
      return <CheckCircle style={{ color: 'var(--green-500)', alignSelf: 'center' }} data-testid={`${task.id}-taskcomplete`} />
    }

    if (taskDue > get1WeekDate()) {
      return <PanoramaFishEye style={{ color: 'var(--blue-700)', alignSelf: 'center' }} data-testid={`${task.id}-taskduelater`} />
    } else {
      return <ErrorIcon style={{ color: 'var(--red-700)', alignSelf: 'center' }} data-testid={`${task.id}-taskduesoon`} />
    }
  }

  const get1WeekDate = (): Date => {
    const newDate = new Date()
    newDate.setDate(newDate.getDate() + 7)
    return newDate
  }

  const getIconAndColor = (): void => {
    let pastDue: boolean = false
    let allComplete: boolean = true
    let dueInOneWeek: boolean = false
    let orgKeyForIcon: number = 0

    sortedTasks.forEach(task => {
      const taskDue: Date = TranslateDate(task.dueDate)

      if (orgKeyForIcon === 0) {
        orgKeyForIcon = task.organizationId
      }

      if (!completedStatuses.includes(task.status)) {
        allComplete = false
      }

      if (taskDue < currentDate && !completedStatuses.includes(task.status)) {
        pastDue = true
      }

      if (taskDue < get1WeekDate() && !completedStatuses.includes(task.status)) {
        dueInOneWeek = true
      }
    })

    if (allComplete) {
      setCardIcon(<CheckCircle sx={{ color: 'white', mx: '15%', width: '70%' }} data-testid={`${orgKeyForIcon}allCompleteIcon`} />)
      setStatusColor('var(--green-500)')
      return
    }

    if (pastDue) {
      setCardIcon(<ErrorIcon sx={{ color: 'white', mx: '15%', width: '70%' }} data-testid={`${orgKeyForIcon}pastDueIcon`} />)
      setStatusColor('var(--red-500)')
      return
    }

    if (dueInOneWeek) {
      setCardIcon(<ErrorIcon sx={{ color: 'white', mx: '15%', width: '70%' }} data-testid={`${orgKeyForIcon}dueSoonIcon`} />)
      setStatusColor('var(--yellow-500)')
    } else {
      setCardIcon(<></>)
      setStatusColor('var(--green-500)')
    }
  }

  if (isLoading) {
    return <CircularProgress />
  }

  const accordionStyle: SxProps = { mx: '1em' }

  const getSummary = (): JSX.Element => {
    return <Box sx={{ ml: '1em', my: '.5em' }}>
      <Typography variant='h5' data-testid={`${orgKey}-orgTitle`}>{props.organizationName}</Typography>
      <Typography variant='body1' sx={{ mt: '.5em' }}>{props.implementationPhase === true ? 'Implementation' : 'Planning'} Progress</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress data-testid={`${orgKey}-progressDisplay`} variant="determinate" value={percentProgress} aria-label='plan progress' />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            {`${percentProgress}%`}
          </Typography>
        </Box>
      </Box>
    </Box>
  }

  const getTaskElements = (): JSX.Element => {
    return <Box>
      <Box sx={{ width: '100%' }}>
        <Typography variant='h5'>Done</Typography>
        { done.length === 0 && <Typography variant='body1' data-testid={`${orgKey}-done`} sx={{ my: '1em' }} fontSize='16px' ml='1em'>No Tasks Done</Typography>}
        { done.map((task) => (
          getTaskLine(task)
        ))}
      </Box>
      <Box>
        <Typography variant='h5'>Now</Typography>
        { dueSoon.length === 0 && <Typography variant='body1' data-testid={`${orgKey}-noneDueSoon`} sx={{ my: '1em' }} fontSize='16px' ml='1em'>No Tasks Due Within 1 Week</Typography>}
        { dueSoon.map((task) => (
          getTaskLine(task)
        ))}
      </Box>
      <Box>
        <Typography variant='h5'>Upcoming</Typography>
        { dueLater.length === 0 && <Typography variant='body1' data-testid={`${orgKey}-noneDueLater`} sx={{ my: '1em' }} fontSize='16px' ml='1em'>No Tasks Due In More Than 1 Week</Typography>}
        { dueLater.map((task) => (
          getTaskLine(task)
        ))}
      </Box>
    </Box>
  }

  const reviewTaskNames = new Set<PlanOrganizationTaskName>([
    PlanOrganizationTaskName.EPLITrainerInitialReview,
    PlanOrganizationTaskName.EPLITrainerFinalReview
  ])

  const isPriorityOneReviewTask = (name: string): boolean =>
    reviewTaskNames.has(name as PlanOrganizationTaskName)

  const goToTaskDetails = (task: PlanOrganizationTask): void => {
    const isPriorityTask = (taskName: string): boolean => {
      return [
        PlanOrganizationTaskName.PriorityOne,
        PlanOrganizationTaskName.PriorityTwo,
        PlanOrganizationTaskName.PriorityThree,
        PlanOrganizationTaskName.PriorityFour
      ].includes(taskName as PlanOrganizationTaskName)
    }

    const externalUrl = externalTaskUrls[task.taskName as PlanOrganizationTaskName]
    if (externalUrl != null) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer')
    } else if (task.taskName === PlanOrganizationTaskName.RcnOperations) {
      props.changeTab(AnnualPlanTabs.RcnOperations)
    } else if (isPriorityTask(task.taskName) || isPriorityOneReviewTask(task.taskName)) {
      if (rcnId == null || planId == null) return

      const route = generatePath(
        props.implementationPhase === true
          ? AppRoutes.IMPLEMENTATION_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION_PRIORITY
          : AppRoutes.PLANNING_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION_PRIORITY,
        {
          rcnId,
          planId,
          tab: String(AnnualPlanTabs.PriorityActivities),
          planOrgId: String(task.planOrganizationId),
          planOrgTaskId: String(task.id)
        }
      )
      nav(route)
    } else if (task.taskName === PlanOrganizationTaskName.BudgetOverview) {
      props.changeTab(AnnualPlanTabs.BudgetOverview)
    } else if (task.taskName === PlanOrganizationTaskName.SignContract) {
      props.changeTab(AnnualPlanTabs.SignContract)
    } else if (task.taskName === PlanOrganizationTaskName.ProgressReportEOY) {
      props.changeTab(AnnualPlanTabs.ProgressReports)
    } else if (task.taskName === PlanOrganizationTaskName.ProgressReportMY) {
      if (rcnId == null || planId == null) return

      if (props.implementationPhase === true) {
        props.changeTab(ImplementationDetailsTabs.ProgressReports)
      }

      const route = generatePath(
        AppRoutes.IMPLEMENTATION_PROGRESS_REPORTS_ORGANIZATION_MIDYEAR,
        {
          rcnId: rcnId ?? null,
          planId: planId ?? null,
          tab: String(props.implementationPhase === true ? ImplementationDetailsTabs.ProgressReports : AnnualPlanTabs.ProgressReports),
          planOrgId: String(task.planOrganizationId)
        }
      )
      nav(route)
    }
  }

  const noChangeTasks = [
    PlanOrganizationTaskName.EPLITrainerFinalReview,
    PlanOrganizationTaskName.EPLITrainerInitialReview
  ]
  const submitPrefixOnlyTasks = [PlanOrganizationTaskName.PeerToPeerDataCollectionSystem,
    PlanOrganizationTaskName.CoachingCapacityTool,
    PlanOrganizationTaskName.FocusStudentDataBOY,
    PlanOrganizationTaskName.FocusStudentDataEOY,
    PlanOrganizationTaskName.CetaBOY,
    PlanOrganizationTaskName.CetaEOY,
    PlanOrganizationTaskName.ProgressReportEOY,
    PlanOrganizationTaskName.ProgressReportMY
  ]
  const submitPrefixAndUpdatesPostfixTasks = [PlanOrganizationTaskName.RcnOperations,
    PlanOrganizationTaskName.PriorityOne,
    PlanOrganizationTaskName.PriorityTwo,
    PlanOrganizationTaskName.PriorityThree,
    PlanOrganizationTaskName.PriorityFour
  ]
  const getTaskLine = (task: PlanOrganizationTask): JSX.Element => {
    const getName = (): string => {
      if (noChangeTasks.includes(task.taskName as PlanOrganizationTaskName) || props.implementationPhase !== true) {
        return task.taskName
      } else if (submitPrefixOnlyTasks.includes(task.taskName as PlanOrganizationTaskName)) {
        return `Submit ${task.taskName}`
      } else if (submitPrefixAndUpdatesPostfixTasks.includes(task.taskName as PlanOrganizationTaskName)) {
        return `Submit ${task.taskName} Updates`
      }
      return task.taskName
    }

    return <Box
      data-testid='planOrganizationTask'
      key={`${task.id}-${task.taskName}`}
      sx={{ display: 'flex', alignContent: 'center', my: '1em' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '70%' }}>
        {taskIcon(task)}
        <Link
          data-testid={`${task.id}-taskLink`}
          onClick={() => { goToTaskDetails(task) }}
          sx={{
            color: 'var(--blue-700)',
            cursor: 'pointer',
            fontSize: '1em',
            alignSelf: 'center',
            marginLeft: '.625em',
            overflowWrap: 'break-word',
            whiteSpace: 'break-spaces'
          }}
        >{getName()}</Link>
        {task.itemsNeedingReviewCount > 0 && (
          <Chip
            label={`${task.itemsNeedingReviewCount} item${task.itemsNeedingReviewCount !== 1 ? 's' : ''} need${task.itemsNeedingReviewCount === 1 ? 's' : ''} review`}
            size="small"
            sx={{ ml: '0.5em', alignSelf: 'center', bgcolor: 'var(--orange-300)' }}
          />
        )}
      </Box>

      <Box sx={{ float: 'right', flexDirection: 'row', width: '30%', paddingRight: '2em' }}>
        <Typography variant='body2' sx={{ float: 'left', alignSelf: 'center', color: 'var(--blue-700)', mr: '1em' }}>{task.status}</Typography>
        <Typography variant='body1' sx={{ float: 'right', alignSelf: 'center', color: 'var(--blue-700)' }}>{new Date(task.dueDate).toLocaleDateString()}</Typography>
      </Box>

    </Box>
  }

  return <IconAccordion
    dataTestId='planOrganizationTaskProgressCard'
    expandButtonKey={`${orgKey}-expandButton`}
    color={statusColor}
    icon={cardIcon}
    expanded={false}
    baseStyle={accordionStyle}
    accordionSummary={getSummary()}
    accordionDetails={getTaskElements()}
  />
}
